import * as React from 'react';
import { useMutation, useQueries } from '@tanstack/react-query';
import { AlertBanner, DatePicker, Modal, Select, Skeleton, Textarea, TextInput, useLocale } from '@ministry/ui/internal';
import { bsToAd, instantToBs, type BsDate as _BsDate } from '@ministry/nepali-date';

import {
  classifyFile,
  getFile,
  linkDartaChalani,
  updateFile,
  type ClassifyFileInput,
  type FileListRow,
  type LinkDartaChalaniInput,
  type UpdateFileInput,
} from '../lib/files.js';
import {
  bulkReassignFiles,
  reassignFile,
  reopenFile,
  type BulkReassignItem,
  type BulkReassignResult,
  type ReassignFileInput,
  type ReopenFileInput,
} from '../lib/workflow.js';
import { ApiError, NetworkError } from '../lib/api-client.js';
import { CONFIDENTIALITY_LEVELS, DEFAULT_REASSIGNMENT_REASON_CODE, REASSIGNMENT_REASONS } from '../lib/file-lifecycle.js';
import { useT } from '../i18n/strings.js';

/**
 * Five small write modals for the file-detail actions this build adds beyond the
 * forward/approve/reject/comment set `MovementActionModal` already covers:
 * edit (`PATCH`), darta-chalani (`PUT`), classification (`POST .../classification`),
 * reopen (`POST .../reopen`) and reassign (`POST .../reassign`).
 *
 * **The `CONFLICT_HOLDER_CHANGED` gap, verified by reading the server, not assumed:**
 * `file-service.ts`'s `updateFile`/`classifyFile`/`linkDartaChalani` call `refuse(
 * 'CONFLICT_HOLDER_CHANGED', reason)` with NO `details` — unlike `movement-service.ts`'s
 * conflict, which carries `currentState` (`{version, statusCode, holderUserId}`) all the
 * way to the response. `file-routes.ts`'s `sendOutcomeFailure` only ever forwards
 * `fieldErrors`, never `details`. So Edit/Classify/Darta-Chalani conflicts below render a
 * generic "this file changed, reload it" banner (no current-state detail to show); Reopen
 * and Reassign — which go through `movement-service.ts` — get the fuller rendering
 * `MovementActionModal` already established, because the data is actually there. Flagged
 * in the handback: the fix is adding `details: outcome.currentState` to
 * `sendOutcomeFailure` in `file-routes.ts`, mirroring what `workflow-routes.ts` already does.
 */

interface BaseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fileId: string;
  expectedVersion: number;
  onSuccess: () => void;
  onStaleConflict: () => void;
}

function genericConflictBanner(t: (k: string, v?: Record<string, string | number>) => string, onReload: () => void): React.ReactNode {
  return (
    <AlertBanner variant="critical" title={t('fileDetail.conflict.title')}>
      <p>{t('fileDetail.conflict.body')}</p>
      <button type="button" className="mt-1 text-sm font-semibold text-text-link underline" onClick={onReload}>
        {t('fileDetail.conflict.reload')}
      </button>
    </AlertBanner>
  );
}

function movementConflictBanner(
  t: (k: string, v?: Record<string, string | number>) => string,
  details: Record<string, unknown> | null,
  onReload: () => void,
): React.ReactNode {
  const statusCode = typeof details?.statusCode === 'string' ? details.statusCode : '—';
  return (
    <AlertBanner variant="critical" title={t('movement.error.conflict.title')}>
      <p>{t('movement.error.conflict.body', { status: statusCode, holder: t('movement.error.conflict.holderOther') })}</p>
      <button type="button" className="mt-1 text-sm font-semibold text-text-link underline" onClick={onReload}>
        {t('fileDetail.conflict.reload')}
      </button>
    </AlertBanner>
  );
}

/** `PATCH /files/{id}` — subject + due date (SCREEN_SPECS.md I8's "attachment additions … follow I7's rules"; edit itself is named in §13.2 but not spec'd screen-by-screen, so this mirrors I9's own conflict/loading discipline). */
export function EditFileModal({
  open,
  onOpenChange,
  fileId,
  expectedVersion,
  currentSubject,
  currentDueAt,
  onSuccess,
  onStaleConflict,
}: BaseModalProps & { currentSubject: string; currentDueAt: string | null }): React.JSX.Element {
  const t = useT();
  const [subject, setSubject] = React.useState(currentSubject);
  const [dueDate, setDueDate] = React.useState<_BsDate | undefined>(currentDueAt ? instantToBs(new Date(currentDueAt)) : undefined);

  React.useEffect(() => {
    if (open) {
      setSubject(currentSubject);
      setDueDate(currentDueAt ? instantToBs(new Date(currentDueAt)) : undefined);
    }
  }, [open, currentSubject, currentDueAt]);

  const mutation = useMutation({
    mutationFn: () => {
      const input: UpdateFileInput = {
        expectedVersion,
        subject: subject.trim() !== currentSubject ? subject.trim() : undefined,
        dueAt: dueDate ? `${isoDate(dueDate)}T00:00:00.000Z` : undefined,
      };
      return updateFile(fileId, input);
    },
    onSuccess: () => {
      onOpenChange(false);
      onSuccess();
    },
  });

  const isConflict = mutation.error instanceof ApiError && mutation.error.code === 'CONFLICT_HOLDER_CHANGED';
  const subjectInvalid = subject.trim().length > 0 && subject.trim().length < 3;

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (mutation.isPending) return;
        onOpenChange(next);
      }}
      title={t('fileDetail.edit.title')}
      confirmLabel={mutation.isPending ? t('fileDetail.edit.submitting') : t('fileDetail.edit.submit')}
      cancelLabel={t('movement.cancel')}
      loading={mutation.isPending}
      confirmDisabled={subjectInvalid}
      onConfirm={isConflict ? undefined : () => mutation.mutate()}
    >
      <div className="flex flex-col gap-4">
        {isConflict
          ? genericConflictBanner(t, () => {
              onOpenChange(false);
              onStaleConflict();
            })
          : mutation.isError
            ? renderGenericError(t, mutation.error)
            : null}
        <Textarea
          id="edit-file-subject"
          label={t('fileDetail.edit.subject')}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          errorText={subjectInvalid ? t('fileIntake.file.subject.error') : undefined}
        />
        <DatePicker id="edit-file-due" label={t('fileDetail.edit.dueDate')} value={dueDate} onChange={setDueDate} />
      </div>
    </Modal>
  );
}

/** `PUT /files/{id}/darta-chalani` — A-004's "linked later", now actually editable (was display-only). */
export function DartaChalaniModal({
  open,
  onOpenChange,
  fileId,
  expectedVersion,
  currentDartaNumber,
  currentChalaniNumber,
  onSuccess,
  onStaleConflict,
}: BaseModalProps & { currentDartaNumber: string | null; currentChalaniNumber: string | null }): React.JSX.Element {
  const t = useT();
  const [dartaNumber, setDartaNumber] = React.useState(currentDartaNumber ?? '');
  const [chalaniNumber, setChalaniNumber] = React.useState(currentChalaniNumber ?? '');
  const [registerDate, setRegisterDate] = React.useState<_BsDate | undefined>(undefined);

  React.useEffect(() => {
    if (open) {
      setDartaNumber(currentDartaNumber ?? '');
      setChalaniNumber(currentChalaniNumber ?? '');
      setRegisterDate(undefined);
    }
  }, [open, currentDartaNumber, currentChalaniNumber]);

  const mutation = useMutation({
    mutationFn: () => {
      const input: LinkDartaChalaniInput = {
        expectedVersion,
        dartaNumber: dartaNumber.trim() === '' ? null : dartaNumber.trim(),
        chalaniNumber: chalaniNumber.trim() === '' ? null : chalaniNumber.trim(),
        registerDate: registerDate ? isoDate(registerDate) : undefined,
      };
      return linkDartaChalani(fileId, input);
    },
    onSuccess: () => {
      onOpenChange(false);
      onSuccess();
    },
  });

  const isConflict = mutation.error instanceof ApiError && mutation.error.code === 'CONFLICT_HOLDER_CHANGED';

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (mutation.isPending) return;
        onOpenChange(next);
      }}
      title={t('fileDetail.darta.editTitle')}
      confirmLabel={mutation.isPending ? t('fileDetail.edit.submitting') : t('fileDetail.edit.submit')}
      cancelLabel={t('movement.cancel')}
      loading={mutation.isPending}
      onConfirm={isConflict ? undefined : () => mutation.mutate()}
    >
      <div className="flex flex-col gap-4">
        {isConflict
          ? genericConflictBanner(t, () => {
              onOpenChange(false);
              onStaleConflict();
            })
          : mutation.isError
            ? renderGenericError(t, mutation.error)
            : null}
        <TextInput id="darta-number" label={t('fileIntake.darta.dartaNumber')} value={dartaNumber} onChange={(e) => setDartaNumber(e.target.value)} />
        <TextInput id="chalani-number" label={t('fileIntake.darta.chalaniNumber')} value={chalaniNumber} onChange={(e) => setChalaniNumber(e.target.value)} />
        <DatePicker id="darta-register-date" label={t('fileIntake.darta.registerDate')} value={registerDate} onChange={setRegisterDate} />
      </div>
    </Modal>
  );
}

/** `POST /files/{id}/classification` — asymmetric rule (section+ raises, ministry+ lowers) is server-enforced; this form does not attempt to pre-filter. */
export function ClassifyModal({
  open,
  onOpenChange,
  fileId,
  expectedVersion,
  currentLevel,
  onSuccess,
  onStaleConflict,
}: BaseModalProps & { currentLevel: string }): React.JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const [level, setLevel] = React.useState(currentLevel);
  const [reason, setReason] = React.useState('');
  const [touched, setTouched] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setLevel(currentLevel);
      setReason('');
      setTouched(false);
    }
  }, [open, currentLevel]);

  const mutation = useMutation({
    mutationFn: () => {
      const input: ClassifyFileInput = { confidentialityLevel: level, reason: reason.trim(), expectedVersion };
      return classifyFile(fileId, input);
    },
    onSuccess: () => {
      onOpenChange(false);
      onSuccess();
    },
  });

  const isConflict = mutation.error instanceof ApiError && mutation.error.code === 'CONFLICT_HOLDER_CHANGED';
  const reasonMissing = touched && reason.trim() === '';

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (mutation.isPending) return;
        onOpenChange(next);
      }}
      title={t('fileDetail.classify.title')}
      confirmLabel={mutation.isPending ? t('fileDetail.edit.submitting') : t('fileDetail.classify.submit')}
      cancelLabel={t('movement.cancel')}
      loading={mutation.isPending}
      confirmDisabled={reason.trim() === ''}
      onConfirm={
        isConflict
          ? undefined
          : () => {
              setTouched(true);
              if (reason.trim() === '') return;
              mutation.mutate();
            }
      }
    >
      <div className="flex flex-col gap-4">
        {isConflict
          ? genericConflictBanner(t, () => {
              onOpenChange(false);
              onStaleConflict();
            })
          : mutation.isError
            ? renderGenericError(t, mutation.error)
            : null}
        <Select
          id="classify-level"
          label={t('fileDetail.classify.level')}
          value={level}
          onValueChange={setLevel}
          options={CONFIDENTIALITY_LEVELS.map((lvl) => ({ value: lvl.code, label: locale === 'ne' ? lvl.labelNe : lvl.labelEn }))}
        />
        <Textarea
          id="classify-reason"
          label={t('fileDetail.classify.reason')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onBlur={() => setTouched(true)}
          errorText={reasonMissing ? t('fileDetail.classify.reason.required') : undefined}
          required
        />
      </div>
    </Modal>
  );
}

/** `POST /files/{id}/reopen` — only valid from `rejected` (`409 CONFLICT_STATE` otherwise). */
export function ReopenModal({ open, onOpenChange, fileId, expectedVersion, onSuccess, onStaleConflict }: BaseModalProps): React.JSX.Element {
  const t = useT();
  const [remarks, setRemarks] = React.useState('');
  const [touched, setTouched] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setRemarks('');
      setTouched(false);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => {
      const input: ReopenFileInput = { remarks: remarks.trim(), expectedVersion };
      return reopenFile(fileId, input);
    },
    onSuccess: () => {
      onOpenChange(false);
      onSuccess();
    },
  });

  const isConflict = mutation.error instanceof ApiError && (mutation.error.code === 'CONFLICT_HOLDER_CHANGED' || mutation.error.code === 'CONFLICT_STATE');
  const remarksMissing = touched && remarks.trim() === '';

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (mutation.isPending) return;
        onOpenChange(next);
      }}
      title={t('fileDetail.reopen.title')}
      description={t('fileDetail.reopen.body')}
      confirmLabel={mutation.isPending ? t('fileDetail.edit.submitting') : t('fileDetail.reopen.submit')}
      cancelLabel={t('movement.cancel')}
      loading={mutation.isPending}
      confirmDisabled={remarks.trim() === ''}
      onConfirm={
        isConflict
          ? undefined
          : () => {
              setTouched(true);
              if (remarks.trim() === '') return;
              mutation.mutate();
            }
      }
    >
      <div className="flex flex-col gap-4">
        {isConflict
          ? movementConflictBanner(t, mutation.error instanceof ApiError ? mutation.error.details : null, () => {
              onOpenChange(false);
              onStaleConflict();
            })
          : mutation.isError
            ? renderGenericError(t, mutation.error)
            : null}
        <Textarea
          id="reopen-remarks"
          label={t('fileDetail.reopen.remarks')}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          onBlur={() => setTouched(true)}
          errorText={remarksMissing ? t('fileDetail.reopen.remarks.required') : undefined}
          required
        />
      </div>
    </Modal>
  );
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * `POST /files/{id}/reassign` — a supervisor moving a file, not the holder's own forward,
 * so `GET /files/{id}/valid-next-holders` (`own`-scoped to the CURRENT holder) is the
 * wrong endpoint here and a supervisor calling it would 403. No `movement.reassign`-scoped
 * target-listing endpoint exists in this build, so the target is a plain user-id field
 * rather than the combobox `MovementActionModal` uses for forward — flagged loudly in the
 * handback as the real gap (the fix is a reassign-scoped variant of valid-next-holders).
 */
export function ReassignModal({ open, onOpenChange, fileId, expectedVersion, onSuccess, onStaleConflict }: BaseModalProps): React.JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const [toUserId, setToUserId] = React.useState('');
  const [reasonCode, setReasonCode] = React.useState(DEFAULT_REASSIGNMENT_REASON_CODE);
  const [remarks, setRemarks] = React.useState('');
  const [touched, setTouched] = React.useState<{ toUserId?: boolean; remarks?: boolean }>({});

  React.useEffect(() => {
    if (open) {
      setToUserId('');
      setReasonCode(DEFAULT_REASSIGNMENT_REASON_CODE);
      setRemarks('');
      setTouched({});
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => {
      const input: ReassignFileInput = { toUserId: toUserId.trim(), reasonCode, remarks: remarks.trim(), expectedVersion };
      return reassignFile(fileId, input);
    },
    onSuccess: () => {
      onOpenChange(false);
      onSuccess();
    },
  });

  const isConflict = mutation.error instanceof ApiError && mutation.error.code === 'CONFLICT_HOLDER_CHANGED';
  const targetInvalid = touched.toUserId && !UUID_PATTERN.test(toUserId.trim());
  const remarksMissing = touched.remarks && remarks.trim() === '';
  const canSubmit = UUID_PATTERN.test(toUserId.trim()) && remarks.trim() !== '';

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (mutation.isPending) return;
        onOpenChange(next);
      }}
      title={t('fileDetail.reassign.title')}
      description={t('fileDetail.reassign.body')}
      confirmLabel={mutation.isPending ? t('fileDetail.edit.submitting') : t('fileDetail.reassign.submit')}
      cancelLabel={t('movement.cancel')}
      loading={mutation.isPending}
      confirmDisabled={!canSubmit}
      onConfirm={
        isConflict
          ? undefined
          : () => {
              setTouched({ toUserId: true, remarks: true });
              if (!canSubmit) return;
              mutation.mutate();
            }
      }
    >
      <div className="flex flex-col gap-4">
        {isConflict
          ? movementConflictBanner(t, mutation.error instanceof ApiError ? mutation.error.details : null, () => {
              onOpenChange(false);
              onStaleConflict();
            })
          : mutation.isError
            ? renderGenericError(t, mutation.error)
            : null}
        <TextInput
          id="reassign-target"
          label={t('fileDetail.reassign.target')}
          value={toUserId}
          onChange={(e) => setToUserId(e.target.value.trim())}
          onBlur={() => setTouched((prev) => ({ ...prev, toUserId: true }))}
          errorText={targetInvalid ? t('fileIntake.applicant.required') : undefined}
          required
        />
        <Select
          id="reassign-reason"
          label={t('fileDetail.reassign.reason')}
          value={reasonCode}
          onValueChange={setReasonCode}
          options={REASSIGNMENT_REASONS.map((r) => ({ value: r.code, label: locale === 'ne' ? r.labelNe : r.labelEn }))}
          required
        />
        <Textarea
          id="reassign-remarks"
          label={t('fileDetail.reassign.remarks')}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          onBlur={() => setTouched((prev) => ({ ...prev, remarks: true }))}
          errorText={remarksMissing ? t('fileDetail.reassign.remarks.required') : undefined}
          required
        />
      </div>
    </Modal>
  );
}

function renderGenericError(t: (k: string, v?: Record<string, string | number>) => string, error: unknown): React.ReactNode {
  if (error instanceof ApiError) {
    if (error.code === 'FORBIDDEN_OWNERSHIP') {
      return <AlertBanner variant="critical" title={t('movement.error.ownership.title')}>{t('movement.error.ownership.body')}</AlertBanner>;
    }
    if (error.code === 'CONFLICT_STATE') {
      return <AlertBanner variant="critical" title={t('movement.error.state.title')}>{t('movement.error.state.body')}</AlertBanner>;
    }
    if (error.retryable) {
      return <AlertBanner variant="critical" title={t('movement.error.network')} />;
    }
    return <AlertBanner variant="critical" title={t('movement.error.generic')} />;
  }
  if (error instanceof NetworkError) {
    return <AlertBanner variant="critical" title={t('movement.error.network')} />;
  }
  return null;
}

function isoDate(bs: _BsDate): string {
  const ad = bsToAd(bs);
  return `${String(ad.year).padStart(4, '0')}-${String(ad.month).padStart(2, '0')}-${String(ad.day).padStart(2, '0')}`;
}

export interface BulkReassignModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The selected register rows, not bare ids — this modal shows tracking codes throughout, never a raw uuid list. */
  rows: readonly FileListRow[];
  /** Called once, after the server has answered — the caller invalidates the register and clears selection. */
  onCompleted: (result: BulkReassignResult) => void;
}

/**
 * `POST /files/bulk-reassign` (§13.3) had a client function and zero UI before this pass
 * — "a transferred officer's whole caseload in one call" with no row selection to call it
 * from. Row selection is `DataTable`'s own existing `selectedIds`/`onSelectionChange`
 * (unused until this pass); this is what a selection turns into.
 *
 * **The `expectedVersion` gap, verified rather than assumed**: `GET /files` (what
 * populates the register `rows` this modal receives) does not return `version` — only
 * `GET /files/{id}` does (confirmed by reading `file-routes.ts`'s list-row projection AND
 * a live response). `POST /files/bulk-reassign` requires `expectedVersion` per item, so
 * this modal fetches each selected file's CURRENT version the moment it opens, rather
 * than inventing one — a version fetched seconds before the write is the same
 * optimistic-concurrency guarantee every other write in this app already gets, just
 * assembled from two requests instead of one because the list endpoint does not carry it.
 *
 * **A real, live-verified defect this same pass fixes**: this modal and `ReassignModal`
 * both used to default `reasonCode` to the free-typed string `'workload'`, which is not a
 * row in `file_reassignment_reason` (`transferred`/`on_leave`/`departed`/
 * `workload_rebalance`/`other`) — verified against a running server, EVERY reassignment
 * left at that default answered `422 VALIDATION_FAILED: "No such active reassignment
 * reason."` for every item. Both now use the same `Select` bound to the real vocabulary.
 */
export function BulkReassignModal({ open, onOpenChange, rows, onCompleted }: BulkReassignModalProps): React.JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const [toUserId, setToUserId] = React.useState('');
  const [reasonCode, setReasonCode] = React.useState(DEFAULT_REASSIGNMENT_REASON_CODE);
  const [remarks, setRemarks] = React.useState('');
  const [touched, setTouched] = React.useState<{ toUserId?: boolean; remarks?: boolean }>({});
  const [result, setResult] = React.useState<BulkReassignResult | null>(null);

  React.useEffect(() => {
    if (open) {
      setToUserId('');
      setReasonCode(DEFAULT_REASSIGNMENT_REASON_CODE);
      setRemarks('');
      setTouched({});
      setResult(null);
    }
  }, [open]);

  const versionQueries = useQueries({
    queries: rows.map((row) => ({
      queryKey: ['file-version-for-bulk-reassign', row.id],
      queryFn: () => getFile(row.id),
      enabled: open && result === null,
      staleTime: 0,
    })),
  });

  const versionsLoading = versionQueries.some((q) => q.isLoading);
  const failedVersionRows = rows.filter((_, i) => versionQueries[i]?.isError);
  const readyItems: BulkReassignItem[] = versionQueries
    .map((q, i) => {
      const row = rows[i];
      return q.data && row ? { fileId: row.id, expectedVersion: q.data.version } : null;
    })
    .filter((item): item is BulkReassignItem => item !== null);

  const mutation = useMutation({
    mutationFn: () =>
      bulkReassignFiles({
        items: readyItems,
        toUserId: toUserId.trim(),
        reasonCode,
        remarks: remarks.trim(),
      }),
    onSuccess: (value) => {
      setResult(value);
      onCompleted(value);
    },
  });

  const targetInvalid = touched.toUserId && !UUID_PATTERN.test(toUserId.trim());
  const remarksMissing = touched.remarks && remarks.trim() === '';
  const canSubmit = UUID_PATTERN.test(toUserId.trim()) && remarks.trim() !== '' && readyItems.length > 0 && !versionsLoading;

  const trackingCodeFor = (fileId: string): string => rows.find((r) => r.id === fileId)?.trackingCode ?? fileId;

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (mutation.isPending) return;
        onOpenChange(next);
      }}
      title={t('fileRegister.bulkReassign.title')}
      description={result === null ? t('fileRegister.bulkReassign.body', { count: rows.length }) : undefined}
      confirmLabel={result === null ? (mutation.isPending ? t('fileDetail.edit.submitting') : t('fileRegister.bulkReassign.submit')) : undefined}
      cancelLabel={result === null ? t('movement.cancel') : t('fileDetail.preview.close')}
      loading={mutation.isPending}
      confirmDisabled={!canSubmit}
      onConfirm={
        result !== null
          ? undefined
          : () => {
              setTouched({ toUserId: true, remarks: true });
              if (!canSubmit) return;
              mutation.mutate();
            }
      }
    >
      {result !== null ? (
        <div className="flex flex-col gap-3">
          <AlertBanner
            variant={result.failed === 0 ? 'info' : 'warning'}
            title={t('fileRegister.bulkReassign.resultSummary', { succeeded: result.succeeded, failed: result.failed })}
          />
          <ul className="flex flex-col gap-1">
            {result.results.map((item) => (
              <li
                key={item.fileId}
                className={`flex items-center justify-between gap-2 rounded-md border p-2 text-sm ${
                  item.ok ? 'border-status-good-solid' : 'border-status-critical-solid'
                }`}
              >
                <span className="font-mono">{trackingCodeFor(item.fileId)}</span>
                <span className={item.ok ? 'text-status-good-tint-fg' : 'text-status-critical-tint-fg'}>
                  {item.ok ? t('fileRegister.bulkReassign.itemOk') : (item.reason ?? t('fileRegister.bulkReassign.itemFailed'))}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {mutation.isError ? renderGenericError(t, mutation.error) : null}

          {versionsLoading ? (
            <Skeleton shape="list-row" count={Math.min(rows.length, 4)} />
          ) : failedVersionRows.length > 0 ? (
            <AlertBanner variant="warning" title={t('fileRegister.bulkReassign.someUnavailable')}>
              {failedVersionRows.map((r) => r.trackingCode).join(', ')}
            </AlertBanner>
          ) : null}

          <ul className="flex max-h-32 flex-col gap-1 overflow-y-auto rounded-md bg-surface-base p-2 text-sm text-text-muted">
            {rows.map((row) => (
              <li key={row.id} className="font-mono">
                {row.trackingCode}
              </li>
            ))}
          </ul>

          <TextInput
            id="bulk-reassign-target"
            label={t('fileDetail.reassign.target')}
            value={toUserId}
            onChange={(e) => setToUserId(e.target.value.trim())}
            onBlur={() => setTouched((prev) => ({ ...prev, toUserId: true }))}
            errorText={targetInvalid ? t('fileIntake.applicant.required') : undefined}
            helperText={t('fileRegister.bulkReassign.targetHelper')}
            required
          />
          <Select
            id="bulk-reassign-reason"
            label={t('fileDetail.reassign.reason')}
            value={reasonCode}
            onValueChange={setReasonCode}
            options={REASSIGNMENT_REASONS.map((r) => ({ value: r.code, label: locale === 'ne' ? r.labelNe : r.labelEn }))}
            required
          />
          <Textarea
            id="bulk-reassign-remarks"
            label={t('fileDetail.reassign.remarks')}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            onBlur={() => setTouched((prev) => ({ ...prev, remarks: true }))}
            errorText={remarksMissing ? t('fileDetail.reassign.remarks.required') : undefined}
            required
          />
        </div>
      )}
    </Modal>
  );
}
